import {
  Box,
  IconButton,
  MenuItem,
  makeStyles,
  Theme,
  createStyles,
} from "@material-ui/core";
import GenericPageTitle from "modus/components/generic-page-title/GenericPageTitle";
import React, { useEffect, useCallback } from "react";
import { ApplicationCreationPage } from "../application-creation-page/ApplicationCreationPage";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import { useSelector } from "react-redux";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { IdentityApplication } from "../../../apis/services/ApiService";
import { Breadcrumbs } from "modus/components/generic-page-title/Breadcrumbs";
import { ApplicationSearch, filterApplicationsByText } from "../../components/ApplicationSearch";
import { AppListView } from "../../components/AppListView";
import ApplicationService from "../../components/v2.0/ApplicationService";
import Select from "legacy-components/form/container/Select";

// Define styles using makeStyles
const useStyles = makeStyles((theme: Theme) => createStyles({
  filterIcon: {
    color: "#00548a"
  },
  sortIcon: {
    color: "#00548a",
    paddingTop: "4px"
  },
  iconButton: {
    backgroundColor: "transparent",
    padding: 0,
    marginLeft: "20px",
    '&:hover': {
      backgroundColor: "transparent"
    }
  },
  sortSelect: {
    minWidth: "150px",
    height: "32px"
  }
}));

export const ApplicationDiscoveryPage: React.FC = () => {
  const classes = useStyles();
  const identityEnvironment = useSelector(
    (state: any) => state?.common?.identityEnvironment
  );

  const { state, subscribe } = useAsyncAction(() =>
    ApplicationService.getAllApplication(identityEnvironment, "all")
  );

  const [sortBy, setSortBy] = React.useState<string>("createdAt");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">("asc");
  const [currentSearchText, setCurrentSearchText] = React.useState<string>("");
  const [filteredApplications, setfilteredApplications] =
    React.useState<IdentityApplication[]>();

  const toggleSortDirection = () => {
    setSortDirection((prevDirection) => {
      const newDirection = prevDirection === "asc" ? "desc" : "asc";
      if (filteredApplications) {
        setfilteredApplications((prev) => {
          if (!prev) return prev;
          const sorted = [...prev];
          return applySorting(sorted, sortBy, newDirection);
        });
      }
      return newDirection;
    });
  };

  const applySorting = (
    apps: IdentityApplication[],
    sortField: string,
    direction: "asc" | "desc"
  ) => {
    if (!apps) return apps;

    const sorted = [...apps];
    const sortMultiplier = direction === "asc" ? 1 : -1;

    switch (sortField) {
      case "name":
        return sorted.sort(
          (firstApp, secondApp) =>
            sortMultiplier *
            firstApp.applicationName.localeCompare(secondApp.applicationName)
        );

      case "createdBy":
        return sorted.sort((firstApp, secondApp) => {
          if (!firstApp.createdBy) return sortMultiplier * 1;
          if (!secondApp.createdBy) return sortMultiplier * -1;
          return (
            sortMultiplier *
            firstApp.createdBy.localeCompare(secondApp.createdBy)
          );
        });

      case "createdAt":
        return sorted.sort((firstApp, secondApp) => {
          if (!firstApp.created) return sortMultiplier * 1;
          if (!secondApp.created) return sortMultiplier * -1;

          try {
            const firstAppDate = new Date(firstApp.created);
            const secondAppDate = new Date(secondApp.created);

            const firstTime = firstAppDate.getTime();
            const secondTime = secondAppDate.getTime();

            if (isNaN(firstTime)) return sortMultiplier * 1;
            if (isNaN(secondTime)) return sortMultiplier * -1;

            return sortMultiplier * (secondTime - firstTime);
          } catch (error) {
            return 0;
          }
        });

      default:
        return apps;
    }
  };

  const onSortChange = (event: { target: { value: any } }) => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    filterApplications();
  };

  const filterApplications = useCallback(
    (searchText?: string) => {
      if (state?.value) {
        let apps = [...state.value];

        if (searchText !== undefined) {
          setCurrentSearchText(searchText);
        }
        const textToFilter =
          searchText !== undefined ? searchText : currentSearchText;
        if (textToFilter && textToFilter.trim() !== "") {
          apps = filterApplicationsByText(apps, textToFilter);
        }
        apps = applySorting(apps, sortBy, sortDirection);
        setfilteredApplications(apps);
      } else {
        setfilteredApplications([]);
      }
    },
    [state, sortBy, sortDirection, currentSearchText]
  );

  useEffect(() => {
    if (!identityEnvironment || !subscribe) {
      return;
    }
    subscribe();
  }, [subscribe, identityEnvironment]);

  useEffect(() => {
    if (!state?.value || !sortBy || !sortDirection) {
      return;
    }
    filterApplications();
  }, [state, sortBy, sortDirection, filterApplications]);

  return (
    <Box padding={2} className="app-discovery-page">
      <Breadcrumbs pageTitle={"Applications"} />
      <GenericPageTitle
        title={"Applications"}
        noSkeleton
        controls={
          <Box display={"flex"}>
            <Box ml="15px" />
            <ApplicationCreationPage onSuccess={() => subscribe()} />
          </Box>
        }
      />
      <Box mt="30px" />
      <Box display={"flex"} mb="30px" paddingRight="10px">
        <Box display="flex" alignItems="center">
          <IconButton 
            className={classes.iconButton} 
            disableRipple
          >
            <i
              className={`modus-icons ${classes.filterIcon}`}
              aria-hidden="true"
            >
              filter
            </i>
          </IconButton>
          <ApplicationSearch onSearch={filterApplications} />
        </Box>

        <Box display={"flex"} justifyContent="space-evenly" marginLeft="30px">
          <Select
            id="applicationSort"
            className={`application-sort-select ${classes.sortSelect}`}
            IconComponent={ExpandMoreIcon}
            onChange={onSortChange}
            value={sortBy}
          >
            {[
              { value: "name", label: "Name" },
              { value: "createdBy", label: "Created By" },
              { value: "createdAt", label: "Created At" },
            ].map((sortedField) => {
              return (
                <MenuItem value={sortedField.value} key={sortedField.value}>
                  {sortedField.label}
                </MenuItem>
              );
            })}
          </Select>

          {sortDirection === "asc" ? (
            <IconButton 
              className={classes.iconButton} 
              onClick={toggleSortDirection}
              disableRipple
            >
              <i
                className={`modus-icons ${classes.sortIcon}`}
                aria-hidden="true"
              >
                sort_up
              </i>
            </IconButton>
          ) : (
            <IconButton 
              className={classes.iconButton} 
              onClick={toggleSortDirection}
              disableRipple
            >
              <i
                className={`modus-icons ${classes.sortIcon}`}
                aria-hidden="true"
              >
                sort_down
              </i>
            </IconButton>
          )}
        </Box>
      </Box>

      <AppListView
        state={state}
        applicationList={filteredApplications}
        refresh={subscribe}
      />
    </Box>
  );
};
